import gql from 'graphql-tag';

import type { FeedAssetGroup } from 'src/data/project';
import { executeGqlQuery, type GqlQueryOptions } from 'src/graphql/client';
import { getSingleProp, throwIfResultHasErrors } from 'src/lib/graphql';
import type { BackendPageInfo } from 'src/types/backend';

import { FRAGMENT_ASSET_GROUP } from './schema';

export interface SectionContent {
  id: string;
  name: string;
  assetGroups: FeedAssetGroup[];
  pageInfo: Pick<BackendPageInfo, 'hasNextPage' | 'endCursor'>;
}

interface Params {
  sectionId: string;
  first?: number;
  after?: string | null;
}

const QUERY = gql`
  query ProjectSectionContent($sectionId: ID!, $first: Int, $after: String) {
    projectSection(id: $sectionId) {
      id
      name
      assetGroups(first: $first, after: $after) {
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          ...AssetGroupFields
        }
      }
    }
  }
  ${FRAGMENT_ASSET_GROUP}
`;

// Asking for more than about sixteen groups at once makes `assetGroups` hang until the gateway
// gives up at sixty seconds, so the whole section fails to load rather than loading slowly. Ten
// keeps a page under a second with room to spare, and the rest arrives through infinite scroll.
const PAGE_SIZE = 10;

interface RawSectionContent {
  id: string;
  name: string;
  assetGroups: {
    pageInfo: Pick<BackendPageInfo, 'hasNextPage' | 'endCursor'>;
    nodes?: (FeedAssetGroup | null)[] | null;
  };
}

export const queryProjectSectionContent = async (
  { sectionId, first = PAGE_SIZE, after = null }: Params,
  options?: Pick<GqlQueryOptions, 'abortSignal'>
): Promise<SectionContent | null> => {
  const result = await executeGqlQuery({
    query: QUERY,
    variables: { sectionId, first, after },
    options: {
      abortSignal: options?.abortSignal,
    },
  });
  throwIfResultHasErrors(result);

  const section = getSingleProp<RawSectionContent | null>(result.data);
  if (!section) {
    return null;
  }

  return {
    id: section.id,
    name: section.name,
    assetGroups: (section.assetGroups.nodes ?? []).filter(Boolean) as FeedAssetGroup[],
    pageInfo: section.assetGroups.pageInfo,
  };
};
